// src/components/discovery/ConfidencePicker.jsx
import React, { useState } from "react";

// Confidence is picked by how strong the evidence is, not as a raw percentage.
// Each level still maps to a 0-100 score so cards, charts and the High/Medium/Low
// badge (>=80 / >=50) keep working unchanged.
// Colours ramp from weak to strong evidence; amber and green match the
// Medium / High card badges.
export const CONFIDENCE_LEVELS = [
  {
    value: 25,
    label: "Guess",
    color: { fg: "var(--danger-fg)", soft: "var(--danger-soft)", border: "var(--danger-border)", strong: "var(--danger)" },
  },
  {
    value: 50,
    label: "Some signal",
    color: { fg: "var(--warning-fg)", soft: "var(--warning-soft)", border: "var(--warning-border)", strong: "var(--warning)" },
  },
  {
    value: 75,
    label: "Strong signal",
    color: { fg: "var(--info-fg)", soft: "var(--info-soft)", border: "var(--info-border)", strong: "var(--info)" },
  },
  {
    value: 90,
    label: "Proven",
    color: { fg: "var(--success-fg)", soft: "var(--success-soft)", border: "var(--success-border)", strong: "var(--success)" },
  },
];

export const DEFAULT_CONFIDENCE = CONFIDENCE_LEVELS[0].value;

export const CONFIDENCE_DIMENSIONS = [
  {
    id: "desirability",
    label: "Desirability",
    question: "Do customers want this?",
    hints: {
      25: "Our assumption — no customer input yet",
      50: "A few customers mentioned it",
      75: "A recurring theme across interviews or tickets",
      90: "Customers asked, paid, or a test confirmed it",
    },
  },
  {
    id: "feasibility",
    label: "Feasibility",
    question: "Can we build it?",
    hints: {
      25: "Not looked into yet",
      50: "Engineering thinks it's doable",
      75: "Approach outlined or similar work done before",
      90: "Spike or prototype proved it",
    },
  },
  {
    id: "viability",
    label: "Viability",
    question: "Is it worth it for the business?",
    hints: {
      25: "Value not estimated yet",
      50: "Plausible business case",
      75: "Ties to an OKR or a sized opportunity",
      90: "Backed by revenue, cost, or experiment data",
    },
  },
];

// Cards scored before the picker existed can hold any 0-100 number. Show them
// on the closest level without rewriting the stored score until the user picks.
// 0 / missing means the card was never scored.
export function nearestConfidenceLevel(score) {
  const n = Number(score);
  if (!Number.isFinite(n) || n <= 0) return null;
  return CONFIDENCE_LEVELS.reduce((best, level) =>
    Math.abs(level.value - n) < Math.abs(best.value - n) ? level : best
  );
}

export default function ConfidencePicker({ dimension, value, onChange }) {
  const [hoveredValue, setHoveredValue] = useState(null);
  const level = nearestConfidenceLevel(value);
  const isExact = level && level.value === Number(value);

  let hint = "Not scored yet — pick the level that matches what you know.";
  if (level) {
    hint = dimension.hints[level.value];
    if (!isExact) hint += ` (saved score: ${Math.round(Number(value))})`;
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--fg)" }}>
          {dimension.label}
        </span>
        <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>
          {dimension.question}
        </span>
      </div>
      <div
        role="radiogroup"
        aria-label={`${dimension.label} confidence`}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(96px, 1fr))",
          gap: 6,
        }}
      >
        {CONFIDENCE_LEVELS.map((option) => {
          const selected = level?.value === option.value;
          const hovered = !selected && hoveredValue === option.value;
          const { color } = option;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              title={dimension.hints[option.value]}
              onClick={() => onChange(option.value)}
              onMouseEnter={() => setHoveredValue(option.value)}
              onMouseLeave={() => setHoveredValue(null)}
              style={{
                padding: "7px 8px",
                fontSize: 13,
                fontWeight: selected ? 600 : 500,
                borderRadius: 8,
                cursor: "pointer",
                transition: "background-color 120ms, border-color 120ms, color 120ms",
                border: `1px solid ${
                  selected ? color.strong : hovered ? color.border : "var(--border)"
                }`,
                background: selected || hovered ? color.soft : "var(--bg-surface)",
                color: selected || hovered ? color.fg : "var(--fg-secondary)",
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--fg-muted)" }}>
        {hint}
      </p>
    </div>
  );
}
